import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';
import { t, getLocale } from './i18n';
import { IconRetea } from './IconRetea';

// ============================================================
// Clopotelul cu alerte (19 octombrie 2026, APROBARE_CLIENT_PLAN.md §4.5, pasul 6).
//
// Cand clientul raspunde prin linkul de aprobare (aproba, modifica, cere schimbari), functia
// `aprobare-link` scrie cate o alerta pentru fiecare super admin si manager al clientului.
// Fiecare om isi vede doar alertele lui (RLS) si poate doar sa le marcheze citite.
//
// Cat de des se intreaba baza (§9.1, dupa caderile pe procesor din 12 si 16.10): la
// deschiderea aplicatiei, la intoarcerea in fereastra (cel mult o data la 30 de secunde) si,
// cat fereastra e in fata, o data la 2 minute. Doar numarul celor necitite, o cerere mica;
// lista se citeste abia cand se deschide clopotelul.
//
// Pe calculator si pe telefon se arata acelasi clopotel; numarul vine dintr-un singur
// `useAlerte`, chemat o data in ecranul agentiei, ca baza sa nu fie intrebata de doua ori.
// ============================================================

const LA_2_MINUTE = 120000;
const MINIM_INTRE_CITIRI = 30000;

// ---- Notificarile pe telefon (20 octombrie 2026, macheta acceptata de Luminita) ----
//
// „Pornește notificările" din clopotel: permisiunea browserului, abonamentul la serviciul lui
// de notificari (pe iPhone, al Apple) si un rand in `push_abonamente`. Functia `aprobare-link`
// trimite apoi notificarea la fiecare raspuns al clientului. Ascultatorul e `public/sw-notificari.js`.
// Pe iPhone merge doar din aplicatia pusa pe ecranul principal din Safari (iOS 16.4+): intr-o fila
// de Safari `PushManager` lipseste, iar caseta spune asta.
// Cheia publica e publica prin natura ei (o vede orice browser abonat); cea privata sta doar in
// secretele Supabase (VAPID_PRIVATE_KEY).
const CHEIE_PUBLICA_PUSH = 'BLYHE4zZ6k6pM9IQ9tv3NSrKLwruyYCeid1dWgJxnxCh7yae5CrVs3co51aj7GWqMHcRcjYDrttKzmHaCgz06qg';
const ASCULTATOR = '/sw-notificari.js';

function octetiDinB64(b64) {
  const s = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const brut = window.atob(s);
  return Uint8Array.from(brut, c => c.charCodeAt(0));
}

function numeAparat() {
  const ua = navigator.userAgent || '';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  return 'calculator';
}

const ePeIos = () => /iPhone|iPad/.test(navigator.userAgent || '');
const suportaPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// 'nesuportat' | 'doar-instalat' | 'blocat' | 'oprit' | 'pornit' | null (se afla)
function useNotificari() {
  const [stare, setStare] = useState(null);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState('');

  useEffect(() => {
    let activ = true;
    (async () => {
      if (!suportaPush()) { if (activ) setStare(ePeIos() ? 'doar-instalat' : 'nesuportat'); return; }
      if (Notification.permission === 'denied') { if (activ) setStare('blocat'); return; }
      const reg = await navigator.serviceWorker.getRegistration(ASCULTATOR);
      const ab = reg ? await reg.pushManager.getSubscription() : null;
      if (activ) setStare(ab && Notification.permission === 'granted' ? 'pornit' : 'oprit');
    })().catch(() => { if (activ) setStare('oprit'); });
    return () => { activ = false; };
  }, []);

  async function porneste() {
    setEroare('');
    setLucreaza(true);
    try {
      // Intai permisiunea, chiar in apasare: Safari o cere legata de gestul omului.
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStare(perm === 'denied' ? 'blocat' : 'oprit'); return; }
      const reg = await navigator.serviceWorker.register(ASCULTATOR);
      await navigator.serviceWorker.ready;
      let ab = await reg.pushManager.getSubscription();
      if (!ab) ab = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: octetiDinB64(CHEIE_PUBLICA_PUSH) });
      const j = ab.toJSON();
      await supabase.from('push_abonamente').delete().eq('endpoint', j.endpoint);
      const { error } = await supabase.from('push_abonamente').insert({ endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth, aparat: numeAparat() });
      if (error) throw error;
      setStare('pornit');
    } catch (e) {
      setEroare(t('alerte', 'pushEroare', { motiv: (e && e.message) || String(e) }));
    } finally {
      setLucreaza(false);
    }
  }

  async function opreste() {
    setLucreaza(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(ASCULTATOR);
      const ab = reg ? await reg.pushManager.getSubscription() : null;
      if (ab) {
        await supabase.from('push_abonamente').delete().eq('endpoint', ab.endpoint);
        await ab.unsubscribe();
      }
      setStare('oprit');
    } finally {
      setLucreaza(false);
    }
  }

  return { stare, lucreaza, eroare, porneste, opreste };
}

function CasetaNotificari() {
  const n = useNotificari();
  if (!n.stare || n.stare === 'nesuportat') return null;
  const cutie = { margin: 10, borderRadius: 10, padding: '10px 12px', fontSize: 13, lineHeight: 1.45, color: '#0F3D22' };
  if (n.stare === 'pornit') {
    return (
      <div style={{ ...cutie, background: 'white', border: '1.5px solid #D1E8DA', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>{t('alerte', 'pushPornite', { aparat: numeAparat() === 'calculator' ? t('alerte', 'acestCalculator') : t('alerte', 'acestTelefon') })}</span>
        <button type="button" onClick={n.opreste} disabled={n.lucreaza} style={{ background: 'none', border: 'none', color: '#374151', textDecoration: 'underline', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('alerte', 'pushOpreste')}
        </button>
      </div>
    );
  }
  if (n.stare === 'doar-instalat') {
    return <div style={{ ...cutie, background: '#F9FAFB', border: '1.5px solid #E5E7EB' }}>{t('alerte', 'pushDoarInstalat')}</div>;
  }
  if (n.stare === 'blocat') {
    return <div style={{ ...cutie, background: '#FEF3C7', border: '1.5px solid #F2D58A', color: '#8F5310' }}>{t('alerte', 'pushBlocat')}</div>;
  }
  return (
    <div style={{ ...cutie, background: '#E8F5EE', border: '1.5px solid #C2E0CE' }}>
      <strong style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>{t('alerte', 'pushTitlu')}</strong>
      {t('alerte', 'pushText')}
      {n.eroare && <div role="alert" style={{ color: '#B91C1C', fontWeight: 600, marginTop: 6 }}>{n.eroare}</div>}
      <div>
        <button type="button" onClick={n.porneste} disabled={n.lucreaza}
          style={{ marginTop: 8, background: '#1A6B3C', color: 'white', border: 'none', borderRadius: 9, padding: '8px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          {n.lucreaza ? t('alerte', 'pushSePornesc') : t('alerte', 'pushPorneste')}
        </button>
      </div>
    </div>
  );
}

export function useAlerte(activ) {
  const [necitite, setNecitite] = useState(0);
  const ultima = useRef(0);

  const numara = useCallback(async (fortat = false) => {
    if (!activ) return;
    if (!fortat && Date.now() - ultima.current < MINIM_INTRE_CITIRI) return;
    ultima.current = Date.now();
    const { count, error } = await supabase.from('alerte').select('id', { count: 'exact', head: true }).is('citita_la', null);
    if (!error && typeof count === 'number') setNecitite(count);
  }, [activ]);

  useEffect(() => {
    if (!activ) return undefined;
    numara(true);
    const ceas = setInterval(() => { if (document.visibilityState === 'visible') numara(true); }, LA_2_MINUTE);
    const laIntoarcere = () => { if (document.visibilityState === 'visible') numara(); };
    // Compozitorul a stins alertele unui grup (deschis dintr-o notificare): se renumara pe loc.
    const laSchimbare = () => numara(true);
    document.addEventListener('visibilitychange', laIntoarcere);
    window.addEventListener('focus', laIntoarcere);
    window.addEventListener('nm-alerte-schimbate', laSchimbare);
    return () => {
      clearInterval(ceas);
      document.removeEventListener('visibilitychange', laIntoarcere);
      window.removeEventListener('focus', laIntoarcere);
      window.removeEventListener('nm-alerte-schimbate', laSchimbare);
    };
  }, [activ, numara]);

  // Cifra de pe iconita aplicatiei instalate (20.10.2026): aceeasi ca la clopotel. Ascultatorul de
  // notificari o pune cand aplicatia e inchisa; aici se potriveste cand citesti sau cand se schimba.
  useEffect(() => {
    if (!activ || !('setAppBadge' in navigator)) return;
    const p = necitite > 0 ? navigator.setAppBadge(necitite) : navigator.clearAppBadge();
    if (p && p.catch) p.catch(() => {});
  }, [activ, necitite]);

  return { necitite, setNecitite, numara };
}

const NUME_RETEA = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', threads: 'Threads', x: 'X', youtube: 'YouTube' };

function cand(iso) {
  const d = new Date(iso);
  const azi = new Date();
  const ora = d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === azi.toDateString()) return ora;
  return `${d.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' })} · ${ora}`;
}

// `alerte` = ce intoarce useAlerte; `onDeschide(grupId)` deschide grupul in compozitor.
// `mobil` = varianta din bara de sus a telefonului (lista ocupa toata latimea).
export function ClopotelAlerte({ alerte, onDeschide, mobil = false }) {
  const [deschis, setDeschis] = useState(false);
  const [lista, setLista] = useState(null);
  const cutie = useRef(null);
  const { necitite, setNecitite, numara } = alerte;

  const incarca = useCallback(async () => {
    const { data } = await supabase
      .from('alerte')
      .select('id, tip, creat_la, citita_la, grup_id, postare_id, clienti(nume), postari(retea, format)')
      .order('creat_la', { ascending: false })
      .limit(20);
    setLista(data || []);
  }, []);

  useEffect(() => {
    if (!deschis) return undefined;
    incarca();
    numara(true);
    const afara = e => { if (cutie.current && !cutie.current.contains(e.target)) setDeschis(false); };
    document.addEventListener('mousedown', afara);
    return () => document.removeEventListener('mousedown', afara);
  }, [deschis, incarca, numara]);

  // Deschiderea unei alerte stinge toate alertele grupului ei (19.10.2026, cerut de Luminita):
  // grupul se vede intreg, cu starea fiecarei postari. Raman aprinse doar alertele altor grupuri.
  async function deschideAlerta(a) {
    const acum = new Date().toISOString();
    const deStins = a.grup_id ? supabase.from('alerte').update({ citita_la: acum }).eq('grup_id', a.grup_id).is('citita_la', null)
      : supabase.from('alerte').update({ citita_la: acum }).eq('id', a.id);
    await deStins;
    setLista(prev => (prev || []).map(x => ((a.grup_id ? x.grup_id === a.grup_id : x.id === a.id) && !x.citita_la ? { ...x, citita_la: acum } : x)));
    numara(true);
    setDeschis(false);
    if (a.grup_id && onDeschide) onDeschide(a.grup_id, a.postare_id);
  }

  async function toateCitite() {
    const ids = (lista || []).filter(a => !a.citita_la).map(a => a.id);
    await supabase.from('alerte').update({ citita_la: new Date().toISOString() }).is('citita_la', null);
    setLista(prev => (prev || []).map(a => (ids.includes(a.id) ? { ...a, citita_la: new Date().toISOString() } : a)));
    setNecitite(0);
  }

  return (
    <div ref={cutie} style={{ position: mobil ? 'static' : 'relative' }} onClick={e => e.stopPropagation()}>
      <button type="button" onClick={() => setDeschis(d => !d)} aria-expanded={deschis}
        aria-label={necitite ? t('alerte', 'clopotelNecitite', { n: necitite }) : t('alerte', 'clopotel')}
        title={t('alerte', 'clopotel')}
        style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', color: '#0F3D22' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {necitite > 0 && (
          <span style={{ position: 'absolute', top: 0, right: 0, minWidth: 18, height: 18, borderRadius: 9, background: '#B91C1C', color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
            {necitite > 99 ? '99+' : necitite}
          </span>
        )}
      </button>
      {deschis && (
        <div role="dialog" aria-label={t('alerte', 'titlu')}
          style={{
            position: 'absolute', zIndex: 50, background: 'white', border: '1.5px solid #D1E8DA', borderRadius: 12,
            boxShadow: '0 8px 28px rgba(15,61,34,0.16)', maxHeight: 460, overflowY: 'auto',
            ...(mobil ? { left: 8, right: 8, top: 56 } : { left: 0, top: 40, width: 340 }),
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #E8F5EE' }}>
            <strong style={{ fontSize: 15, color: '#111827' }}>{t('alerte', 'titlu')}</strong>
            {necitite > 0 && (
              <button type="button" onClick={toateCitite} style={{ background: 'none', border: 'none', color: '#1A6B3C', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {t('alerte', 'toateCitite')}
              </button>
            )}
          </div>
          <CasetaNotificari />
          {lista === null && <p style={{ padding: 14, margin: 0, color: '#374151', fontSize: 14 }}>{t('alerte', 'seIncarca')}</p>}
          {lista && !lista.length && <p style={{ padding: 14, margin: 0, color: '#374151', fontSize: 14 }}>{t('alerte', 'niciuna')}</p>}
          {lista && lista.map(a => {
            const p = a.postari || {};
            return (
              <button key={a.id} type="button" onClick={() => deschideAlerta(a)}
                style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', border: 'none', borderBottom: '1px solid #F2F8F4', background: a.citita_la ? 'white' : '#F2F8F4', cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, marginTop: 7, flexShrink: 0, background: a.citita_la ? 'transparent' : '#1A6B3C' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, color: '#111827', fontWeight: a.citita_la ? 500 : 700 }}>
                    {t('alerte', `tip_${a.tip}`, { client: (a.clienti && a.clienti.nume) || '' })}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', marginTop: 2 }}>
                    {p.retea && <IconRetea id={p.retea} size={13} />}
                    {[p.retea && (NUME_RETEA[p.retea] || p.retea), p.format, cand(a.creat_la)].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
